import React from 'react';
import PropTypes from 'prop-types';
import { Alert } from '../../CommonComponents/Alert/Alert';
import UpsertAccountModal from '../../UpsertAccountModal/UpsertAccountModalContainer';
import { Content } from './NetWorthSidebarStyles';
import { VIEWS } from '../../UpsertAccountModal/UpsertAccountModalConstants';
import { TabSlugOptions } from '../../Profile/Accounts/types/tab';
import { EXTERNAL_HOST } from '../../ConsumerArea/ConsumerAreaConstants';
import { OpenNewAccountButton } from './components/OpenNewAccountButton/OpenNewAccountButton';
import { Box } from '../../CommonComponents/OPL/Box/Box';
import { RelatedHouseholds } from './components/RelatedHouseholds/RelatedHouseholds';
import { Text } from '../../CommonComponents/OPL/Text/Text';
import { AccountGroup } from './components/AccountGroup/AccountGroup';
import { NetWorthLink } from './components/NetWorthLink/NetWorthLink';
import { NetWorthSidebarLoading } from './NetWorthSidebar.loading';
import { NetWorthSidebarEmpty } from './NetWorthSidebar.empty';
import { Button } from '../../CommonComponents/OPL/Button/Button';
import { getIsAccountSelected } from './utils/getIsAccountSelected';
import { ManageAkoyaLinkedAccounts } from '../../Profile/Accounts/components/ManageAkoyaLinkedAccounts/ManageAkoyaLinkedAccounts';
import { unmanagedGroupId } from '../../AccountPerformanceReporting/AccountPerformanceReporting.constants';
import { DraggableWrapper } from './components/DraggableSidebar/DraggableWrapper';

const ACCOUNT_PERFORMANCE_REPORTING = 'AccountPerformanceReporting';

const TRANSACTIONS = 'Transactions';

class NetWorthSidebarView extends React.Component {
  constructor(props) {
    super(props);

    this.onAddAccount = this.onAddAccount.bind(this);

    this.state = {
      isAkoyaLinkManagementModalOpen: false,
    };
  }

  componentDidMount() {
    const {
      fetchNetWorthData,
      fetchOrionAccountsData,
      currentClient,
      currentRoute: { controller },
      isFetching,
      dataVersion,
    } = this.props;

    if (
      controller === ACCOUNT_PERFORMANCE_REPORTING ||
      controller === 'ApexAccount' ||
      controller === TRANSACTIONS
    ) {
      if (dataVersion === 0 && !isFetching) {
        fetchNetWorthData(currentClient.Id, true);
      }
    } else {
      fetchNetWorthData(currentClient.Id, true);
    }

    if (controller === ACCOUNT_PERFORMANCE_REPORTING) {
      fetchOrionAccountsData(currentClient.Id);
    }
  }

  onAddAccount() {
    const { openAddAccountModal, hasAccessToLinkAccounts, hasPlaid, akoyaPhase } = this.props;

    if (akoyaPhase !== 'none' || (hasAccessToLinkAccounts && hasPlaid)) {
      openAddAccountModal(VIEWS.PLAID);
    } else {
      openAddAccountModal(VIEWS.ADD);
    }
  }

  render() {
    const {
      errors,
      isFetching,
      accountGroups,
      totalNetWorth,
      currentClient,
      setErrors,
      hasAccessToLinkAccounts,
      onLinkClick,
      currentRoute,
      externalHost,
      hasNetWorthAccess,
      showAddManualAccount,
      onAccountClick,
      hasOrionManagedAccount,
      managedAccountGroups,
      isFinancialPlanEnabled,
      openAddAccountModal,
      hasPlaid,
      unmanagedAccountsLabel,
      akoyaPhase,
    } = this.props;

    const { isAkoyaLinkManagementModalOpen } = this.state;

    const isNetWorthVisible =
      hasNetWorthAccess &&
      ![EXTERNAL_HOST.MONETA, EXTERNAL_HOST.WEALTHCARE, EXTERNAL_HOST.CARSON].includes(
        externalHost,
      );

    const hasPlaidAccountLinking = hasPlaid && hasAccessToLinkAccounts;

    const hasAkoyaAccountLinking = akoyaPhase !== 'none';

    const canAddAccounts =
      ![EXTERNAL_HOST.FTCI, EXTERNAL_HOST.WEALTHCARE].includes(externalHost) &&
      (showAddManualAccount || hasPlaidAccountLinking || hasAkoyaAccountLinking);

    const isMoneta = [EXTERNAL_HOST.MONETA, EXTERNAL_HOST.CARSON].includes(externalHost);

    const showAccountManagementLink =
      isMoneta ||
      (![EXTERNAL_HOST.FTCI, EXTERNAL_HOST.WEALTHCARE].includes(externalHost) &&
        (hasAccessToLinkAccounts || isFinancialPlanEnabled));

    if (isFetching) {
      return <NetWorthSidebarLoading />;
    }

    const showRelatedHouseholds = externalHost !== EXTERNAL_HOST.WEALTHCARE;

    return (
      <DraggableWrapper>
        <UpsertAccountModal
          onOpenAkoyaLinkManagementModal={() => {
            openAddAccountModal(VIEWS.LIST);
            this.setState({ isAkoyaLinkManagementModalOpen: true });
          }}
        />

        {isAkoyaLinkManagementModalOpen && (
          <ManageAkoyaLinkedAccounts
            onClose={() => {
              this.setState({ isAkoyaLinkManagementModalOpen: false });
            }}
            onBack={() => {
              this.setState({ isAkoyaLinkManagementModalOpen: false });
              this.onAddAccount();
            }}
            onExternalAccountLinked={() => {
              this.setState({ isAkoyaLinkManagementModalOpen: false });
            }}
          />
        )}

        {accountGroups.length === 0 ? (
          <NetWorthSidebarEmpty canAddAccounts={canAddAccounts} onAddAccount={this.onAddAccount} />
        ) : (
          <Content>
            <Box p="16">
              <Text variant="tableHeader">Accounts</Text>
            </Box>

            {errors.length > 0 && (
              <Alert
                message={errors}
                shouldScrollOnUpdate={false}
                onRequestClose={() => setErrors([])}
              />
            )}

            {accountGroups.map(accountGroup => (
              <Box mb="16">
                <AccountGroup
                  key={accountGroup.Name}
                  clientId={currentClient.Id}
                  accountGroup={accountGroup}
                  isInitiallyExpanded={
                    (accountGroup.Name === 'Investment' &&
                      currentRoute.controller === ACCOUNT_PERFORMANCE_REPORTING) ||
                    accountGroup.Items.some(account =>
                      getIsAccountSelected(account, currentRoute.params.accountId),
                    )
                  }
                  selectedAccountId={currentRoute.params.accountId}
                  selectedGroupId={currentRoute.params.groupId}
                  isManagedAccountsSelected={
                    currentRoute.controller === ACCOUNT_PERFORMANCE_REPORTING &&
                    !currentRoute.params.groupId &&
                    !currentRoute.params.accountId
                  }
                  isUnmanagedAccountsSelected={
                    currentRoute.controller === ACCOUNT_PERFORMANCE_REPORTING &&
                    currentRoute.params.groupId === unmanagedGroupId
                  }
                  onAccountClick={account => onAccountClick(currentClient, account)}
                  isPerformanceReporting={currentRoute.controller === ACCOUNT_PERFORMANCE_REPORTING}
                  hasOrionManagedAccounts={hasOrionManagedAccount}
                  managedAccountGroups={managedAccountGroups}
                  unmanagedAccountsLabel={unmanagedAccountsLabel}
                />
              </Box>
            ))}

            {isNetWorthVisible && (
              <NetWorthLink
                netWorth={totalNetWorth}
                onClick={() =>
                  onLinkClick('NetWorth', {
                    clientId: currentClient.Id,
                    pivot: 0,
                  })
                }
              />
            )}
          </Content>
        )}

        {showRelatedHouseholds && <RelatedHouseholds clientId={currentClient.Id} />}

        <Box display="flex" flexShrink={0} flexDirection="column" px="16" py="32" css="gap: 16px">
          {canAddAccounts && accountGroups.length !== 0 && (
            <Button leftIcon="far fa-plus" size="large" onClick={this.onAddAccount}>
              Add Account
            </Button>
          )}

          {![EXTERNAL_HOST.MONETA, EXTERNAL_HOST.WEALTHCARE, EXTERNAL_HOST.CARSON].includes(
            externalHost,
          ) && <OpenNewAccountButton clientId={currentClient.Id} />}

          {showAccountManagementLink && (
            <Button
              variant="link"
              color="primary.600"
              onClick={() =>
                ADV.util.Router.go2('ProfileAccounts', {
                  clientId: currentClient.Id,
                  tabSlug: TabSlugOptions.preferences,
                })
              }
            >
              Account Preferences
            </Button>
          )}
        </Box>
      </DraggableWrapper>
    );
  }
}

NetWorthSidebarView.defaultProps = {
  managedAccountGroups: {},
};

NetWorthSidebarView.propTypes = {
  errors: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isFetching: PropTypes.bool.isRequired,
  onLinkClick: PropTypes.func.isRequired,
  accountGroups: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  totalNetWorth: PropTypes.number.isRequired,
  currentClient: PropTypes.shape({
    Id: PropTypes.number.isRequired,
    IsOrionClient: PropTypes.bool.isRequired,
  }).isRequired,
  onAccountClick: PropTypes.func.isRequired,
  setErrors: PropTypes.func.isRequired,
  fetchNetWorthData: PropTypes.func.isRequired,
  currentRoute: PropTypes.shape().isRequired,
  fetchOrionAccountsData: PropTypes.func.isRequired,
  hasOrionManagedAccount: PropTypes.bool.isRequired,
  hasAccessToLinkAccounts: PropTypes.bool.isRequired,
  hasPlaid: PropTypes.bool.isRequired,
  openAddAccountModal: PropTypes.func.isRequired,
  managedAccountGroups: PropTypes.shape({}),
  dataVersion: PropTypes.number.isRequired,
  externalHost: PropTypes.string.isRequired,
  hasNetWorthAccess: PropTypes.bool.isRequired,
  showAddManualAccount: PropTypes.bool.isRequired,
  isFinancialPlanEnabled: PropTypes.bool.isRequired,
  unmanagedAccountsLabel: PropTypes.string.isRequired,
  akoyaPhase: PropTypes.oneOf(['none', 'phase2', 'phase3']).isRequired,
};

export default NetWorthSidebarView;
